import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ComplianceProvider } from '../../contexts/ComplianceContext';
import { Button } from '../ui/button';
import BackButton from '../BackButton';
import { Shield, LayoutDashboard, LogOut, Menu, X, ChevronDown, Clock, Calendar, Users, Banknote, ShieldCheck, Building2, ScrollText, MessageSquare, Settings, UserCircle, Folder, Network } from 'lucide-react';
import { cn } from '../../lib/utils';
import NotificationBell from '../NotificationBell';
import { MENU_ITEMS, loadMenuLayout, supervisorGroups } from '../../lib/adminMenu';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPEN_KEY = 'admin-nav-open-groups';

// Visual-only category accents. SAN yellow stays the brand + active colour.
const GROUP_STYLE = {
  shifts:     { icon: Calendar,      text: 'text-blue-400',   border: 'border-blue-400/60',   bg: 'bg-blue-400/[0.07]' },
  myarea:     { icon: UserCircle,    text: 'text-primary',    border: 'border-primary/60',    bg: 'bg-primary/[0.07]' },
  staff:      { icon: Users,         text: 'text-teal-400',   border: 'border-teal-400/60',   bg: 'bg-teal-400/[0.07]' },
  departments:{ icon: Network,       text: 'text-cyan-400',   border: 'border-cyan-400/60',   bg: 'bg-cyan-400/[0.07]' },
  payroll:    { icon: Banknote,      text: 'text-green-400',  border: 'border-green-400/60',  bg: 'bg-green-400/[0.07]' },
  compliance: { icon: ShieldCheck,   text: 'text-purple-400', border: 'border-purple-400/60', bg: 'bg-purple-400/[0.07]' },
  clients:    { icon: Building2,     text: 'text-orange-400', border: 'border-orange-400/60', bg: 'bg-orange-400/[0.07]' },
  audit:      { icon: ScrollText,    text: 'text-indigo-400', border: 'border-indigo-400/60', bg: 'bg-indigo-400/[0.07]' },
  feedback:   { icon: MessageSquare, text: 'text-pink-400',   border: 'border-pink-400/60',   bg: 'bg-pink-400/[0.07]' },
  system:     { icon: Settings,      text: 'text-slate-400',  border: 'border-slate-400/60',  bg: 'bg-slate-400/[0.07]' },
};
const DEFAULT_GROUP_STYLE = { icon: Folder, text: 'text-slate-400', border: 'border-slate-400/60', bg: 'bg-slate-400/[0.07]' };

function readOpenGroups() {
  try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY)) || []); } catch { return new Set(); }
}

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    axios.get(`${API}/admin/staff-messages/unread-count`)
      .then((r) => setUnreadMessages(r.data.unread || 0))
      .catch(() => {});
  }, []);
  useAutoRefresh(() => {
    axios.get(`${API}/admin/staff-messages/unread-count`)
      .then((r) => setUnreadMessages(r.data.unread || 0))
      .catch(() => {});
  }, { match: ['/messages'], intervalMs: 60000 });
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isSupervisor = user?.role === 'supervisor';

  useEffect(() => {
    let mounted = true;
    if (isSupervisor) {
      setGroups(supervisorGroups());
      return () => { mounted = false; };
    }
    loadMenuLayout(user?.id).then((g) => { if (mounted) setGroups(g); });
    const onUpdate = (e) => { if (mounted) setGroups(e.detail); };
    window.addEventListener('admin-menu-updated', onUpdate);
    return () => { mounted = false; window.removeEventListener('admin-menu-updated', onUpdate); };
  }, [user?.id, isSupervisor, location.pathname]);

  useEffect(() => {
    // Sub-pages (e.g. /admin/shifts/events) belong to their parent menu item.
    const navPath = location.pathname.startsWith('/admin/shifts/') ? '/admin/shifts' : location.pathname;
    const active = groups.find((g) => g.items.includes(navPath));
    if (active && !openGroups.has(active.id)) {
      setOpenGroups((prev) => new Set([...prev, active.id]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, location.pathname]);

  const [openSubmenus, setOpenSubmenus] = useState(() => new Set());
  useEffect(() => {
    // Auto-expand the submenu whose child page is currently open.
    if (location.pathname.startsWith('/admin/shifts/')) {
      setOpenSubmenus((prev) => (prev.has('/admin/shifts') ? prev : new Set([...prev, '/admin/shifts'])));
    }
  }, [location.pathname]);

  const toggleSubmenu = (path) => {
    setOpenSubmenus((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem(OPEN_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isDashboard = location.pathname === (isSupervisor ? '/supervisor' : '/admin');

  return (
    <ComplianceProvider>
    <div className="min-h-screen bg-background">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <span className="font-barlow text-lg tracking-wider text-primary uppercase">San Security</span>
        </div>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-40 w-64 h-full transition-transform duration-300",
        "bg-[hsl(var(--sidebar))] border-r border-[hsl(var(--sidebar-border))]",
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-3 px-6 py-6 border-b border-[hsl(var(--sidebar-border))] flex-shrink-0">
            <Shield className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <span className="font-barlow text-xl tracking-widest text-primary uppercase">San Security</span>
          </div>
          
          {/* Mobile spacer */}
          <div className="lg:hidden h-16 flex-shrink-0" />
          
          {/* Scrollable navigation */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            <nav className="px-3 py-4 space-y-1.5">
              {/* Dashboard (admin) / My Clock In-Out (supervisor) — always visible */}
              <Link
                to={isSupervisor ? '/supervisor' : '/admin'}
                data-testid="nav-dashboard"
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all",
                  "text-sm uppercase tracking-wider font-medium",
                  isDashboard
                    ? "bg-primary/10 text-primary border-l-2 border-primary"
                    : "text-[hsl(var(--sidebar-foreground))] hover:text-foreground hover:bg-[hsl(var(--sidebar-active))]"
                )}
              >
                {isSupervisor ? <Clock className="w-5 h-5" strokeWidth={1.5} /> : <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />}
                {isSupervisor ? 'My Clock In/Out' : 'Dashboard'}
              </Link>

              {groups.map((group) => {
                const isOpen = openGroups.has(group.id);
                const navPathForGroup = location.pathname.startsWith('/admin/shifts/') ? '/admin/shifts' : location.pathname;
                const hasActive = group.items.includes(navPathForGroup);
                const gs = GROUP_STYLE[group.id] || DEFAULT_GROUP_STYLE;
                const GIcon = gs.icon;
                return (
                  <div key={group.id} data-testid={`nav-group-${group.id}`} className={cn('rounded-sm', isOpen && gs.bg)}>
                    <button
                      onClick={() => toggleGroup(group.id)}
                      data-testid={`nav-group-toggle-${group.id}`}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2.5 rounded-sm transition-colors border-l-2",
                        "text-[13px] uppercase tracking-wider font-bold",
                        isOpen || hasActive ? gs.border : 'border-transparent',
                        hasActive && !isOpen
                          ? "text-primary"
                          : "text-[hsl(var(--sidebar-foreground))] hover:text-foreground hover:bg-[hsl(var(--sidebar-active))]"
                      )}
                    >
                      <span className="flex items-center gap-2.5">
                        <GIcon className={cn('w-[18px] h-[18px] flex-shrink-0', gs.text)} strokeWidth={1.75} />
                        {group.title}
                      </span>
                      <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", gs.text, isOpen && "rotate-180")} strokeWidth={2} />
                    </button>
                    {isOpen && (
                      <div className={cn("mt-0.5 mb-1 space-y-0.5 border-l-2 ml-5 pl-2 pb-1.5", gs.border)}>
                        {group.items.map((path) => {
                          const item = MENU_ITEMS[path];
                          if (!item) return null;
                          if (item.children) {
                            const subOpen = openSubmenus.has(path);
                            const childActive = item.children.some((c) => location.pathname === c.path);
                            return (
                              <div key={path}>
                                <button
                                  type="button"
                                  onClick={() => toggleSubmenu(path)}
                                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  className={cn(
                                    "w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-sm transition-all",
                                    "text-xs uppercase tracking-wider font-medium",
                                    childActive && !subOpen
                                      ? "text-primary"
                                      : "text-[hsl(var(--sidebar-foreground))] hover:text-foreground hover:bg-[hsl(var(--sidebar-active))]"
                                  )}
                                >
                                  <span className="flex items-center gap-2.5">
                                    <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                                    {item.label}
                                  </span>
                                  <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", subOpen && "rotate-180")} strokeWidth={1.5} />
                                </button>
                                {subOpen && (
                                  <div className="mt-0.5 mb-1 space-y-0.5 border-l border-[hsl(var(--sidebar-border))] ml-4 pl-2">
                                    {item.children.map((child) => {
                                      const childIsActive = location.pathname === child.path;
                                      return (
                                        <Link
                                          key={child.path}
                                          to={child.path}
                                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                                          onClick={() => setSidebarOpen(false)}
                                          className={cn(
                                            "flex items-center gap-2.5 px-3 py-2 rounded-sm transition-all",
                                            "text-xs uppercase tracking-wider font-medium",
                                            childIsActive
                                              ? "bg-primary/10 text-primary border-l-2 border-primary -ml-[2px]"
                                              : "text-[hsl(var(--sidebar-foreground))] hover:text-foreground hover:bg-[hsl(var(--sidebar-active))]"
                                          )}
                                        >
                                          <item.icon className="w-3.5 h-3.5 flex-shrink-0 opacity-70" strokeWidth={1.5} />
                                          {child.label}
                                        </Link>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          const isActive = location.pathname === path;
                          return (
                            <Link
                              key={path}
                              to={path}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={() => setSidebarOpen(false)}
                              className={cn(
                                "flex items-center gap-2.5 px-3 py-2 rounded-sm transition-all",
                                "text-xs uppercase tracking-wider font-medium",
                                isActive
                                  ? "bg-primary/10 text-primary border-l-2 border-primary -ml-[2px]"
                                  : "text-[hsl(var(--sidebar-foreground))] hover:text-foreground hover:bg-[hsl(var(--sidebar-active))]"
                              )}
                            >
                              <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                              {item.label}
                              {path === '/admin/messages' && unreadMessages > 0 && (
                                <span
                                  className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold"
                                  data-testid="nav-messages-unread-badge"
                                >
                                  {unreadMessages}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        
          {/* Fixed Footer: User info + Sign Out */}
          <div className="p-4 border-t border-[hsl(var(--sidebar-border))] space-y-3 flex-shrink-0">
            {/* User info */}
            <div className="px-4 py-3 bg-[hsl(var(--sidebar-active))] rounded-sm" data-testid="sidebar-user-info">
              <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{isSupervisor ? 'Supervisor' : 'Administrator'}</p>
            </div>
            
            {/* Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Sign Out</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="pt-16 lg:pt-0">
          <div className="px-3 sm:px-6 pt-3 lg:pt-4 flex items-center justify-between">
            <BackButton />
            <NotificationBell />
          </div>
          {children}
        </div>
      </main>
    </div>
    </ComplianceProvider>
  );
}
